import { useState, useEffect, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConnectionStore, useEnvironmentStore } from '@/shared/stores'
import { useEnvironmentModalStore } from '@/shared/stores/environment-modal-store'
import { DebugServerIndicator } from '@/shared/components/DebugServerIndicator'
import { UpdateIndicator } from './UpdateIndicator'

const HEALTH_CHECK_INTERVAL = 30000 // 30 seconds

export function Header() {
  const navigate = useNavigate()
  const { summary, checkSystemHealth } = useConnectionStore()
  const { active } = useEnvironmentStore()
  const { openModal } = useEnvironmentModalStore()
  const [isHovering, setIsHovering] = useState(false)
  const [isHealthHovering, setIsHealthHovering] = useState(false)

  // Initialize health check and set up periodic refresh
  useEffect(() => {
    checkSystemHealth()

    const interval = setInterval(() => {
      checkSystemHealth()
    }, HEALTH_CHECK_INTERVAL)

    return () => clearInterval(interval)
  }, [checkSystemHealth])

  const isProd = active === 'prod'

  const healthDotStyle: CSSProperties = {
    ...styles.healthDot,
    background: summary.unhealthy === 0 ? 'var(--success)' : 'var(--warning)',
    boxShadow: summary.unhealthy === 0
      ? '0 0 6px var(--success)'
      : '0 0 6px var(--warning)',
  }

  const envBadgeStyle: CSSProperties = {
    ...styles.envBadgeBase,
    ...(isProd ? styles.envBadgeProd : styles.envBadgeDev),
    ...(isHovering ? styles.envBadgeHover : {}),
    cursor: 'pointer',
  }

  return (
    <header style={styles.header} data-tauri-drag-region data-trace-shell="header">
      {/* Left: Environment + Health */}
      <div style={styles.leftSection} data-tauri-drag-region>
        <button
          style={envBadgeStyle}
          onClick={openModal}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          title="Switch environment"
          aria-label={`Current environment: ${active.toUpperCase()}. Click to switch environment.`}
          data-testid="app-shell-environment-switcher"
        >
          <div className="live-dot" style={styles.liveDotSmall} />
          {active.toUpperCase()}
        </button>

        {/* Health Indicator — navigates to /health */}
        <button
          style={{
            ...styles.healthIndicator,
            ...(isHealthHovering ? styles.healthIndicatorHover : {}),
          }}
          onClick={() => navigate('/health')}
          onMouseEnter={() => setIsHealthHovering(true)}
          onMouseLeave={() => setIsHealthHovering(false)}
          title="View system health dashboard"
          aria-label={`System health: ${summary.healthy} of ${summary.total} services healthy. Click to view system health dashboard.`}
          data-testid="app-shell-health-indicator"
        >
          <div style={healthDotStyle} />
          <span style={styles.healthText}>
            {summary.healthy}/{summary.total} healthy
          </span>
        </button>
      </div>

      {/* Right: Update pill + Debug Server */}
      <div style={styles.rightSection}>
        {/* Subtle "update available" pill. Renders nothing unless the
            background poll has found a newer version in the Drive release
            folder (or an update is mid-flight). */}
        <UpdateIndicator />
        {/* Debug HTTP Server Indicator (task 2901). Renders nothing outside Tauri. */}
        <DebugServerIndicator />
      </div>
    </header>
  )
}

const styles: Record<string, CSSProperties> = {
  header: {
    display: 'flex',
    height: '48px',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--surface-strip)',
    padding: '0 20px',
    backdropFilter: 'blur(24px)',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  envBadgeBase: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '9999px',
    padding: '2px 10px',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    border: 'none',
    fontFamily: 'inherit',
    transition: 'all 200ms ease',
  },
  envBadgeProd: {
    border: '1px solid rgba(34, 197, 94, 0.2)',
    background: 'rgba(34, 197, 94, 0.08)',
    color: 'var(--success)',
  },
  envBadgeDev: {
    border: '1px solid rgba(245, 158, 11, 0.2)',
    background: 'rgba(245, 158, 11, 0.08)',
    color: 'var(--warning)',
  },
  envBadgeHover: {
    filter: 'brightness(1.3)',
    transform: 'scale(1.05)',
  },
  liveDotSmall: {
    width: '5px',
    height: '5px',
  },

  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  healthIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--surface-elevated-strong)',
    padding: '4px 10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 200ms ease',
  },
  healthIndicatorHover: {
    background: 'var(--surface-elevated-stronger, var(--surface-elevated-strong))',
    border: '1px solid var(--border-default)',
    transform: 'scale(1.02)',
  },
  healthDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  healthText: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },

}
